/* eslint-disable react/forbid-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import { TRANSLATION_FILES } from '../constants/translation';

const COUNTDOWN_NOT_STARTED = 1;
const COUNTDOWN_STARTED = 2;
const COUNTDOWN_FINISHED = 3;

class Countdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      remainingTime: 0,
      status: COUNTDOWN_NOT_STARTED,
      intervalId: null,
    };
  }

  componentDidMount = () => {
    this.startTimeout = setTimeout(this.start, this.props.startDelay);
  }

  componentDidUpdate = (prevProps) => {
    // A finished countdown has cleared its interval; a new target must start it again.
    // Skip invalid targets: NaN never equals NaN, so they would restart on every update.
    const next = moment(this.props.targetDate);
    if (next.isValid() && moment(prevProps.targetDate).valueOf() !== next.valueOf()) {
      clearTimeout(this.startTimeout);
      clearInterval(this.state.intervalId);
      this.start();
    }
  }

  componentWillUnmount = () => {
    clearTimeout(this.startTimeout);
    clearInterval(this.state.intervalId);
  }

  start = () => {
    const timer = setInterval(() => {
      this.tick();
    }, this.props.interval);

    this.setState({
      status: COUNTDOWN_STARTED,
      intervalId: timer,
    });

    this.tick();
  }

  // Never negative: an expired target renders zeros, not "0-9 m" or "NaN d".
  calculateRemainingTime = () => Math.max(0, moment(this.props.targetDate).diff(moment()))

  addLeadingZero = (value) => {
    if (value < 10) {
      return `0${value.toString()}`;
    }
    return value;
  }

  tick = () => {
    // Branch on the freshly computed value, not this.state: inside a React batch (e.g. a
    // restart from componentDidUpdate) setState is deferred and the state read would be stale.
    const remainingTime = this.calculateRemainingTime();
    this.setState({ remainingTime }, () => {
      if (this.props.onChange) this.props.onChange(remainingTime);
    });

    if (remainingTime <= 0) {
      this.setState({
        status: COUNTDOWN_FINISHED,
      });

      if (this.props.onFinished) {
        this.props.onFinished();
      }
      clearInterval(this.state.intervalId);
    }
  }

  renderRemainingTime = () => {
    const html = [];
    const { format, leadingZero, timeSeparator, showLegend, showUnit, t } = this.props;
    const { remainingTime } = this.state;
    if (format.day) {
      let daysLeft = moment.duration(remainingTime).asDays();
      if (leadingZero) {
        daysLeft = this.addLeadingZero(daysLeft);
      }
      let dayText = Math.trunc(daysLeft);
      if (showLegend || showUnit) {
        dayText = t(`{{count}}${showLegend ? ' days' : ''}${showUnit ? ' d' : ''}`, { count: Math.trunc(daysLeft) });
      }
      html.push(
        <span className="react-cntdwn-day" key="day">
          {dayText}{timeSeparator}
        </span>,
      );
    }

    if (format.hour) {
      let hours = moment.duration(remainingTime).get('hours');
      if (leadingZero) {
        hours = this.addLeadingZero(hours);
      }
      let hourText = hours;
      if (showLegend || showUnit) {
        hourText = t(`{{count}}${showLegend ? ' hours' : ''}${showUnit ? ' h' : ''}`, { count: hours });
      }
      html.push(
        <span className="react-cntdwn-hour" key="hour">
          {hourText}{timeSeparator}
        </span>,
      );
    }

    if (format.minute) {
      let minutes = moment.duration(remainingTime).get('minutes');
      if (leadingZero) {
        minutes = this.addLeadingZero(minutes);
      }
      let minuteText = minutes;
      if (showLegend || showUnit) {
        minuteText = t(`{{count}}${showLegend ? ' minutes' : ''}${showUnit ? ' m' : ''}`, { count: minutes });
      }
      html.push(
        <span className="react-cntdwn-minute" key="minute">
          {minuteText}{timeSeparator}
        </span>,
      );
    }

    if (format.second) {
      let seconds = moment.duration(remainingTime).get('seconds');
      if (leadingZero) {
        seconds = this.addLeadingZero(seconds);
      }
      let secondText = seconds;
      if (showLegend || showUnit) {
        secondText = t(`{{count}}${showLegend ? ' seconds' : ''}${showUnit ? ' s' : ''}`, { count: seconds });
      }
      html.push(
        <span className="react-cntdwn-second" key="second">
          {secondText}
        </span>,
      );
    }

    return html;
  }

  render = () => {
    if (this.state.status === COUNTDOWN_NOT_STARTED) {
      return (
        <span />
      );
    }
    return (
      <div className="react-cntdwn-timer">
        {this.renderRemainingTime()}
      </div>
    );
  }
}

Countdown.propTypes = {
  targetDate: PropTypes.instanceOf(Date).isRequired,
  interval: PropTypes.number,
  startDelay: PropTypes.number,
  onFinished: PropTypes.func,
  format: PropTypes.object,
  timeSeparator: PropTypes.string,
  leadingZero: PropTypes.bool,
  showLegend: PropTypes.bool,
  showUnit: PropTypes.bool,
  onChange: PropTypes.func,
};

Countdown.defaultProps = {
  interval: 1000,
  startDelay: 0,
  format: {
    hour: 'HH',
    minute: 'mm',
    second: 'ss',
  },
  timeSeparator: ' ',
  leadingZero: false,
  showLegend: false,
  showUnit: false,
};

export default withTranslation(TRANSLATION_FILES.screens)(Countdown);



// WEBPACK FOOTER //
// ./src/components/CountdownAdvance.jsx